// The shortlist-timeframe readout's vocabulary and pure presentation logic
// (Monday 3204302464): what happened, by the timeframe the buyer chose at the
// start-choice picker. Kept out of the page so it can be unit-tested — the
// admin app has no DOM test harness, and the honesty rules below (an unknown
// reads "-", never 0; every rate says what it rests on) are exactly the kind
// of thing a page silently gets wrong.

import type { TimeframePicker, TimeframeWindow } from "@recruiter/shared/types";

/** The roll-up windows the route accepts, in display order. Mirrors the
 *  `TimeframeWindow` Literal in `backend/web/schemas.py`. */
export const TIMEFRAME_WINDOWS: readonly TimeframeWindow[] = ["7d", "30d", "90d"];

export const TIMEFRAME_WINDOW_LABELS: Record<TimeframeWindow, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
};

export function isTimeframeWindow(value: unknown): value is TimeframeWindow {
  return typeof value === "string" && (TIMEFRAME_WINDOWS as readonly string[]).includes(value);
}

/** `?window=` parser: a known window or absent (the page falls back to a month). */
export function parseTimeframeWindow(value: unknown): TimeframeWindow | undefined {
  return isTimeframeWindow(value) ? value : undefined;
}

/** "2 hours", "12 hours" — the picker's own words for a timeframe. */
export function hoursLabel(hours: number): string {
  return hours === 1 ? "1 hour" : `${hours} hours`;
}

/** A rate as a percentage with one decimal. Null is "-", never 0%: an
 *  unmeasured cell is UNKNOWN, and printing zero would read as a collapse. */
export function formatRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return "-";
  return `${Math.round(rate * 1000) / 10}%`;
}

/** A mean with one decimal, "-" when nothing was observed. */
export function formatMean(mean: number | null | undefined): string {
  if (mean === null || mean === undefined || !Number.isFinite(mean)) return "-";
  return `${Math.round(mean * 10) / 10}`;
}

/** What a figure rests on, printed beside it: "of 12". Empty when nothing. */
export function restsOn(count: number): string {
  return count > 0 ? `of ${count}` : "";
}

export interface PickerSegment {
  key: "started" | "searched" | "dropped";
  label: string;
  count: number;
  /** Over the shown projects; null when nobody was shown. */
  share: number | null;
}

/** The picker's three outcomes as the segments of one 100% bar. They partition
 *  the shown projects (the backend counts a search pick only when no concierge
 *  start followed the show), so the shares add up to one — and to nothing at
 *  all when nobody was shown, where the bar is not drawn. */
export function pickerSegments(picker: TimeframePicker): PickerSegment[] {
  const shown = picker.shown > 0 ? picker.shown : null;
  const share = (count: number) => (shown === null ? null : count / shown);
  return [
    { key: "started", label: "Started Mira", count: picker.started, share: share(picker.started) },
    {
      key: "searched",
      label: "Went to the search",
      count: picker.searched,
      share: share(picker.searched),
    },
    { key: "dropped", label: "Left", count: picker.dropped, share: share(picker.dropped) },
  ];
}

/** Whether a rate rests on too little to read much into: the same threshold
 *  the cohort cards use for a thin side. */
export const THIN_COUNT = 10;

export function isThin(count: number): boolean {
  return count > 0 && count < THIN_COUNT;
}
